// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { locale } from '@/i18n';
import { localDateToDate } from '@/services/localDate';

/**
 * "12 Jul" in the app's language. Falls back to the round's UTC timestamp for rounds that were
 * never given a play date.
 */
export function formatShortDate(playDate: string | null, playedAtUtc?: string | null): string {
  const date = playDate ? localDateToDate(playDate) : playedAtUtc ? new Date(playedAtUtc) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short' }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

/** Merit points read better without a trailing `.0`. */
export function formatMerits(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** `2nd`, `3rd`… in English; other languages fall back to the bare number. */
export function formatPosition(position: number, currentLocale = locale()): string {
  if (!currentLocale.startsWith('en')) {
    return `${position}`;
  }

  const lastTwo = position % 100;
  if (lastTwo >= 11 && lastTwo <= 13) {
    return `${position}th`;
  }

  switch (position % 10) {
    case 1:
      return `${position}st`;
    case 2:
      return `${position}nd`;
    case 3:
      return `${position}rd`;
    default:
      return `${position}th`;
  }
}
